import React from 'react';
import PropTypes from 'prop-types';

import {dispatch, connect} from 'utils/state_manager';

import {Box, Button, IconButton, Stack, Tooltip, Typography} from 'views/components';
import {RefreshOutlined} from 'views/icons';
import {MessageBubble, MessageComposer, Translate, translate} from 'views/module';
import communication from 'utils/backend/server/communication/communication';
import userUtil from 'utils/miscellaneous/user/user_util';
import moment from 'moment';

const ChannelThread = (props) => {
  const {
    conversationRn,
    channelBackend,
    channelTitle,
    messages,
    isLoading,
    loadFailed,
    connectionStatus,
    refreshMessages,
    user,
  } = props;

  const isDisconnected = connectionStatus === communication.CHANNEL_CONNECTION_STATUS.DISCONNECTED;

  const bottomRef = React.useRef(null);

  React.useEffect(() => {
    if (messages.length === 0) return;
    bottomRef.current?.scrollIntoView({block: 'end'});
  }, [messages]);

  const sortedMessages = React.useMemo(() => {
    return [...messages].sort((first, second) => new Date(first.createdAt) - new Date(second.createdAt));
  }, [messages]);

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          p: '18px 20px',
          borderBottom: '1px solid #ECECF1',
        }}
      >
        <Typography sx={{fontSize: '16px', fontWeight: 700, color: '#050A14'}}>{channelTitle}</Typography>
        <Tooltip title={translate('inbox.Thread.Refresh')}>
          <IconButton
            aria-label={translate('inbox.Thread.Refresh')}
            disabled={isLoading}
            onClick={refreshMessages}
            sx={{
              'flexShrink': 0,
              'color': '#63708A',
              '&:hover': {backgroundColor: '#F7F8FA'},
            }}
          >
            <RefreshOutlined sx={{fontSize: 18}} />
          </IconButton>
        </Tooltip>
      </Box>
      {
        isDisconnected &&
          <Stack
            direction='row'
            sx={{
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              px: '20px',
              py: '10px',
              backgroundColor: '#FEF2F2',
              borderBottom: '1px solid #F6C9C0',
            }}
          >
            <Typography sx={{fontSize: '12px', fontWeight: 500, color: '#B4321F'}}>
              <Translate expression='inbox.Thread.Disconnected' />
            </Typography>
            <Button
              onClick={() => channelBackend.connection.reconnect()}
              sx={{
                'flexShrink': 0,
                'height': '26px',
                'px': '10px',
                'border': '1px solid #E3A99C',
                'borderRadius': '7px',
                'backgroundColor': '#FFFFFF',
                'color': '#B4321F',
                'fontSize': '12px',
                'fontWeight': 700,
                'lineHeight': '16px',
                'textTransform': 'none',
                'boxShadow': 'none',
                'minWidth': 0,
                '&:hover': {backgroundColor: '#FFF5F3', borderColor: '#D98A79', boxShadow: 'none'},
              }}
              variant='outlined'
            >
              {translate('inbox.Thread.Reconnect')}
            </Button>
          </Stack>
      }
      <Box
        sx={{
          'flex': 1,
          'overflowY': 'auto',
          'p': '18px 20px',
          'scrollbarColor': '#CFCFD4 transparent',
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#CFCFD4',
            borderRadius: '999px',
          },
        }}
      >
        {
          isLoading &&
            <Typography sx={{fontSize: '14px', fontWeight: 400, color: '#9A9BAE'}}>
              <Translate expression='inbox.Thread.Loading' />
            </Typography>
        }
        {
          loadFailed &&
            <Typography sx={{fontSize: '14px', fontWeight: 400, color: 'error.main'}}>
              <Translate expression='inbox.Thread.LoadFailed' />
            </Typography>
        }
        {
          !isLoading && !loadFailed && messages.length === 0 &&
            <Typography sx={{fontSize: '14px', fontWeight: 400, color: '#9A9BAE'}}>
              <Translate expression='inbox.Thread.Empty' />
            </Typography>
        }
        <Stack sx={{gap: 1}}>
          {
            sortedMessages.map((message) => {
              const isCurrentUserMessage = message.requesterRn === user?.userRn;
              const senderName = userUtil.getFullName(
                  message.requester?.firstName,
                  message.requester?.lastName,
              ).trim() || message.requesterRn;

              return (
                <MessageBubble
                  key={message.messageRn}
                  isOwn={isCurrentUserMessage}
                  fullName={senderName}
                  timestampLabel={message.createdAt ? moment(message.createdAt).format('MMM D, h:mm A') : ''}
                  messageText={message.messageText}
                  files={message.files}
                  showAuthorRow
                />
              );
            })
          }
        </Stack>
        <div ref={bottomRef} />
      </Box>
      <MessageComposer
        conversationRn={conversationRn}
        onSend={channelBackend.message.send}
        placeholder={translate('inbox.Thread.Placeholder')}
        sendLabel={translate('inbox.Thread.Send')}
        sendFailedLabel={translate('inbox.Thread.SendFailed')}
      />
    </>
  );
};

ChannelThread.propTypes = {
  conversationRn: PropTypes.string.isRequired,
  channelBackend: PropTypes.object.isRequired,
  channelTitle: PropTypes.string,
  messages: PropTypes.arrayOf(PropTypes.object).isRequired,
  isLoading: PropTypes.bool.isRequired,
  loadFailed: PropTypes.bool.isRequired,
  connectionStatus: PropTypes.string,
  refreshMessages: PropTypes.func.isRequired,
  user: PropTypes.object,
};

const mapStateToProps = (state) => ({user: state.user});

export default connect(mapStateToProps, dispatch)(React.memo(ChannelThread));
